import { useMemo } from "react";
import {
  useGetDeviceCountQuery,
  useGetAssetSummaryQuery,
  useGetAssetSummaryNotWorkingQuery,
  useGetActiveAlertsQuery,
} from "features/icccApiSlice";
import {
  SEVERITY,
  HEALTH,
  HEALTH_RANK,
  DEFAULT_POLL_MS,
  compareCategories,
  timeRangeMs,
} from "../model/constants";

const emptyGroup = (code) => ({
  code,
  total: 0,
  healthy: 0,
  warning: 0,
  critical: 0,
  unknown: 0,
});

const aggregateAlertsByAsset = (alerts) => {
  const map = new Map();
  alerts.forEach((a) => {
    if (a.health === HEALTH.HEALTHY) return;
    const cur = map.get(a.assetId);
    if (!cur) {
      map.set(a.assetId, {
        assetId: a.assetId,
        name: a.nodeName || `Asset #${a.assetId}`,
        category: a.category,
        ipAddress: a.ipAddress,
        location: a.location,
        count: 1,
        worstSeverity: a.severity,
        health: a.health,
        lastRaisedAt: a.raisedAt,
        lastMessage: a.message,
      });
      return;
    }
    cur.count += 1;
    if ((HEALTH_RANK[a.health] || 0) > (HEALTH_RANK[cur.health] || 0)) {
      cur.health = a.health;
      cur.worstSeverity = a.severity;
    }
    if (a.raisedAt > (cur.lastRaisedAt || 0)) {
      cur.lastRaisedAt = a.raisedAt;
      cur.lastMessage = a.message;
    }
    if ((!cur.name || cur.name.startsWith("Asset #")) && a.nodeName) cur.name = a.nodeName;
  });
  return map;
};

export default function useDashboardData({ pollMs = DEFAULT_POLL_MS, timeRange = "all" } = {}) {
  const opts = { pollingInterval: pollMs };
  const countQ = useGetDeviceCountQuery(undefined, opts);
  const summaryQ = useGetAssetSummaryQuery(undefined, opts);
  const notWorkingQ = useGetAssetSummaryNotWorkingQuery(undefined, opts);
  const alertsQ = useGetActiveAlertsQuery(undefined, opts);

  const derived = useMemo(() => {
    const totalDevices = countQ.data || 0;
    const totals = summaryQ.data || []; // [{code,total}]
    const notWorking = notWorkingQ.data || []; // [{code,total}]
    const alerts = alertsQ.data || []; // normalized

    const groups = new Map();
    const ensure = (code) => {
      if (!groups.has(code)) groups.set(code, emptyGroup(code));
      return groups.get(code);
    };
    totals.forEach(({ code, total }) => {
      ensure(code).total += total;
    });
    const notWorkingByCode = new Map(notWorking.map((r) => [r.code, r.total]));

    const assetMap = aggregateAlertsByAsset(alerts);
    assetMap.forEach(({ health, category }) => {
      const g = ensure(category);
      if (health === HEALTH.CRITICAL) g.critical += 1;
      else if (health === HEALTH.WARNING) g.warning += 1;
    });

    groups.forEach((g) => {
      const unhealthy = notWorkingByCode.has(g.code)
        ? notWorkingByCode.get(g.code)
        : g.critical + g.warning;
      g.critical = Math.min(g.critical, unhealthy);
      g.warning = Math.min(g.warning, Math.max(0, unhealthy - g.critical));
      g.unknown = Math.max(0, unhealthy - g.critical - g.warning);
      g.healthy = Math.max(0, g.total - unhealthy);
    });

    const perGroup = Array.from(groups.values()).sort(compareCategories);

    const rollup = perGroup.reduce(
      (acc, g) => {
        acc.total += g.total;
        acc.healthy += g.healthy;
        acc.warning += g.warning;
        acc.critical += g.critical;
        acc.unknown += g.unknown;
        return acc;
      },
      { total: 0, healthy: 0, warning: 0, critical: 0, unknown: 0 }
    );
    
    if (totalDevices > rollup.total) {
      rollup.unknown += totalDevices - rollup.total;
      rollup.total = totalDevices;
    }

    const windowMs = timeRangeMs(timeRange);
    const hasTimestamps = alerts.some((a) => a.raisedAt > 0);
    const since = windowMs ? Date.now() - windowMs : 0;
    const inWindow =
      !windowMs || !hasTimestamps
        ? alerts
        : alerts.filter((a) => a.raisedAt === 0 || a.raisedAt >= since);

    const alertsSummary = {
      total: inWindow.length,
      critical: inWindow.filter((a) => a.severity === SEVERITY.CRITICAL).length,
      warning: inWindow.filter((a) => a.health === HEALTH.WARNING).length,
    };

    const bySeverityGroupMap = new Map();
    inWindow.forEach((a) => {
      if (!bySeverityGroupMap.has(a.category)) {
        bySeverityGroupMap.set(a.category, { group: a.category, critical: 0, warning: 0, total: 0 });
      }
      const row = bySeverityGroupMap.get(a.category);
      row.total += 1;
      if (a.severity === SEVERITY.CRITICAL) row.critical += 1;
      else if (a.health === HEALTH.WARNING) row.warning += 1;
    });

    const healthByAsset = {};
    assetMap.forEach((v, k) => {
      healthByAsset[k] = v.health;
    });
    const affectedDevices = Array.from(assetMap.values()).sort(
      (a, b) => HEALTH_RANK[b.health] - HEALTH_RANK[a.health] || b.count - a.count
    );

    return {
      rollup,
      perGroup,
      alertsSummary,
      bySeverityGroup: Array.from(bySeverityGroupMap.values()).sort((a, b) => b.total - a.total),
      activeAlerts: alerts,
      healthByAsset,
      affectedDevices,
    };
  }, [countQ.data, summaryQ.data, notWorkingQ.data, alertsQ.data, timeRange]);

  return {
    ...derived,
    isLoading:
      countQ.isLoading || summaryQ.isLoading || notWorkingQ.isLoading || alertsQ.isLoading,
    isFetching:
      countQ.isFetching || summaryQ.isFetching || notWorkingQ.isFetching || alertsQ.isFetching,
    isError: countQ.isError || summaryQ.isError || notWorkingQ.isError || alertsQ.isError,
    refetch: () => {
      countQ.refetch();
      summaryQ.refetch();
      notWorkingQ.refetch();
      alertsQ.refetch();
    },
    lastUpdated: Math.max(
      countQ.fulfilledTimeStamp || 0,
      summaryQ.fulfilledTimeStamp || 0,
      notWorkingQ.fulfilledTimeStamp || 0,
      alertsQ.fulfilledTimeStamp || 0
    ),
  };
}
